import { useCallback, useMemo, useRef, useState } from 'react';

import { useCameraListData } from 'modules/cameras/hooks/useCameraListData';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { toast } from 'react-toastify';

import { ICameraCCTV, IProjectPreferences } from '@api/models';
import CctvCameraMapControls from '../CctvCameraMapControls/CctvCameraMapControls';
import CctvCamerasMapView from '../CctvCamerasMapView/CctvCamerasMapView';
import { updateCctvCamera } from '@api/services/cctv-camera.service';
import { updateProjectMapPreference } from '@api/services/project.service';

type CameraMapPanelProps = {
    cameras: ICameraCCTV[];
    loadingMessage?: string;
    onSaveCamera: (cameraId: string, payload: ICameraCCTV) => void;
};

const CctvCameraMapPanel = ({ cameras, loadingMessage = 'Loading Camera Map', onSaveCamera }: CameraMapPanelProps) => {
    const { clientId, projectId } = useParams();

    // camera list cache helpers
    // const { updateCamera: updateCameraInList } = useCameraListData(clientId as string, projectId as string);

    // view controls state
    const [selectedCamera, setSelectedCamera] = useState<ICameraCCTV | null>(null);
    const [cameraAngle, setCameraAngle] = useState(0);
    const [cameraRange, setCameraRange] = useState(0);
    const [cameraFOV, setCameraFOV] = useState(0);

    // location edit state
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [tempLatitude, setTempLatitude] = useState('');
    const [tempLongitude, setTempLongitude] = useState('');
    const mapViewRef = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(null);

    const [isSaving, setIsSaving] = useState(false);

    // Keep original location at the moment editing starts (for Cancel)
    const editOriginRef = useRef<{ lat: string; lng: string } | null>(null);

    const handleCameraSelect = useCallback((camera: ICameraCCTV) => {
        setSelectedCamera(camera);

        setCameraAngle(Number(camera.angle));
        setCameraRange(Number(camera.distance));
        setCameraFOV(Number(camera.fieldOfView));

        setIsEditingLocation(false);
        setTempLatitude(camera.location?.latitude?.toString() ?? '');
        setTempLongitude(camera.location?.longitude?.toString() ?? '');

        editOriginRef.current = null;
    }, []);

    const tempMarkerPosition = useMemo(() => {
        if (!isEditingLocation) return null;
        const lat = parseFloat(tempLatitude);
        const lng = parseFloat(tempLongitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { lat, lng };
    }, [isEditingLocation, tempLatitude, tempLongitude]);

    const handleMapPickLocation = useCallback(
        (pos: google.maps.LatLngLiteral) => {
            if (!isEditingLocation) return;
            setTempLatitude(pos.lat.toFixed(6));
            setTempLongitude(pos.lng.toFixed(6));
        },
        [isEditingLocation]
    );

    const handleMarkerDragEnd = useCallback((pos: google.maps.LatLngLiteral) => {
        setTempLatitude(pos.lat.toFixed(6));
        setTempLongitude(pos.lng.toFixed(6));
    }, []);

    /**
     * Change Location / Apply Location (same button)
     * - When entering editing: store origin coords for Cancel
     * - When applying (exiting editing): update selectedCamera + list cache immediately (preview)
     */
    const handleLocationChange = useCallback(() => {
        if (!selectedCamera) return;

        if (!isEditingLocation) {
            // entering edit mode -> store origin for Cancel
            editOriginRef.current = {
                lat: selectedCamera.location?.latitude?.toString() ?? '',
                lng: selectedCamera.location?.longitude?.toString() ?? '',
            };
            setIsEditingLocation(true);
            return;
        }

        // applying (turning OFF editing)
        const lat = parseFloat(tempLatitude);
        const lng = parseFloat(tempLongitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            // keep editing ON if invalid
            return;
        }

        const applied: ICameraCCTV = {
            ...selectedCamera,
            location: {
                ...(selectedCamera.location as any),
                latitude: lat.toString(),
                longitude: lng.toString(),
            } as any,
        };

        // ✅ preview update: selected + list cache (so marker/polygon moves immediately)
        setSelectedCamera(applied);
        onSaveCamera(applied._id, applied);

        setIsEditingLocation(false);
    }, [isEditingLocation, selectedCamera, tempLatitude, tempLongitude, onSaveCamera]);

    /**
     * Cancel Location:
     * - revert temp coords back to origin
     * - exit editing
     * - also revert selectedCamera + list cache to origin (preview rollback)
     */
    const handleCancelLocation = useCallback(() => {
        if (!selectedCamera) return;

        const origin = editOriginRef.current;
        const originLat = origin?.lat ?? selectedCamera.location?.latitude?.toString() ?? '';
        const originLng = origin?.lng ?? selectedCamera.location?.longitude?.toString() ?? '';

        setTempLatitude(originLat);
        setTempLongitude(originLng);

        // rollback preview if we had changed selectedCamera during edit
        const reverted: ICameraCCTV = {
            ...selectedCamera,
            location: {
                ...(selectedCamera.location as any),
                latitude: originLat,
                longitude: originLng,
            } as any,
        };

        setSelectedCamera(reverted);
        onSaveCamera(reverted._id, reverted);

        setIsEditingLocation(false);
        editOriginRef.current = null;
    }, [selectedCamera, onSaveCamera]);

    const handleCloseControls = useCallback(() => {
        setSelectedCamera(null);
        setIsEditingLocation(false);
        editOriginRef.current = null;
    }, []);

    const handleGetCurrentValues = useCallback(() => {
        if (!selectedCamera) return;

        setCameraAngle(Number(selectedCamera.angle));
        setCameraRange(Number(selectedCamera.distance));
        setCameraFOV(Number(selectedCamera.fieldOfView));

        setTempLatitude(selectedCamera.location?.latitude?.toString() ?? '');
        setTempLongitude(selectedCamera.location?.longitude?.toString() ?? '');
    }, [selectedCamera]);

    const updateCameraMutation = useMutation((payload: Partial<ICameraCCTV>) =>
        updateCctvCamera(clientId as string, projectId as string, selectedCamera!._id, payload)
    );

    /**
     * Save:
     * - persists angle/range/fov + location (from temp fields)
     * - updates selected + list cache with server response (or merges payload if response is not full)
     */
    const handleSaveValues = useCallback(async () => {
        if (!selectedCamera) return;

        const lat = parseFloat(tempLatitude);
        const lng = parseFloat(tempLongitude);

        // if user typed invalid values, block save
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        setIsSaving(true);
        try {
            const payload: Partial<ICameraCCTV> = {
                ...selectedCamera,
                storage: selectedCamera.storageName as string,
                angle: cameraAngle,
                distance: cameraRange,
                fieldOfView: cameraFOV,
                location: {
                    ...(selectedCamera.location as any),
                    latitude: lat.toString(),
                    longitude: lng.toString(),
                } as any,
            };

            const updated = await updateCameraMutation.mutateAsync(payload);

            toast.success('Camera View Updated');

            // If API returns full camera -> use it
            // If API returns partial -> merge fallback
            const updatedCamera: ICameraCCTV = (updated as any)?._id
                ? (updated as any)
                : ({
                      ...selectedCamera,
                      ...payload,
                      location: payload.location
                          ? ({ ...selectedCamera.location, ...payload.location } as any)
                          : selectedCamera.location,
                  } as any);

            setSelectedCamera(updatedCamera);
            onSaveCamera(updatedCamera._id, updatedCamera);

            setIsEditingLocation(false);
            editOriginRef.current = null;
        } catch (err: any) {
            // 🔴 error toast
            const message = err?.response?.data?.message || err?.message || 'Failed to update camera view';

            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    }, [
        selectedCamera,
        tempLatitude,
        tempLongitude,
        cameraAngle,
        cameraRange,
        cameraFOV,
        updateCameraMutation,
        onSaveCamera,
    ]);

    const hasUnsavedChanges = useMemo(() => {
        if (!selectedCamera) return false;

        return (
            cameraAngle !== +selectedCamera.angle ||
            cameraRange !== +selectedCamera.distance ||
            cameraFOV !== +selectedCamera.fieldOfView ||
            isEditingLocation
        );
    }, [selectedCamera, cameraAngle, cameraRange, cameraFOV, isEditingLocation]);

    const updateProjectMutation = useMutation((payload: any) =>
        updateProjectMapPreference(clientId as string, projectId as string, payload)
    );

    const handleSaveMapPreference = useCallback(async () => {
        const view = mapViewRef.current;
        if (!view) {
            toast.error('Map view not available yet');
            return;
        }

        const payload: Pick<IProjectPreferences, 'mapZoom'> = {
            mapZoom: view.zoom,
            // mapCenter: {
            //     latitude: view.center.lat,
            //     longitude: view.center.lng,
            // },
        };
        setIsSaving(true);

        try {
            await updateProjectMutation.mutateAsync({
                preferences: payload, // or payload directly
            });

            toast.success('Map preference saved');
            setIsSaving(false);
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to save map preference';

            toast.error(message);
            setIsSaving(false);
        }
    }, [updateProjectMutation]);

    const handleMapViewChange = useCallback((view: { center: google.maps.LatLngLiteral; zoom: number }) => {
        mapViewRef.current = view;
    }, []);

    return (
        <div className="relative w-full h-screen py-6 rounded-lg mt-auto">
            {selectedCamera && (
                <CctvCameraMapControls
                    selectedCamera={selectedCamera}
                    cameraAngle={cameraAngle}
                    cameraRange={cameraRange}
                    cameraFOV={cameraFOV}
                    setCameraAngle={setCameraAngle}
                    setCameraRange={setCameraRange}
                    setCameraFOV={setCameraFOV}
                    isEditingLocation={isEditingLocation}
                    tempLatitude={tempLatitude}
                    tempLongitude={tempLongitude}
                    setTempLatitude={setTempLatitude}
                    setTempLongitude={setTempLongitude}
                    onToggleLocationEdit={handleLocationChange}
                    onCancelLocationEdit={handleCancelLocation}
                    onClose={handleCloseControls}
                    onGetCurrent={handleGetCurrentValues}
                    onSave={handleSaveValues}
                    isSaving={isSaving}
                    showUnsavedChanges={hasUnsavedChanges}
                    setMapPreference={handleSaveMapPreference}
                />
            )}

            <CctvCamerasMapView
                cameras={cameras}
                loadingMessage={loadingMessage}
                selectedCamera={selectedCamera}
                cameraAngle={cameraAngle}
                cameraRange={cameraRange}
                cameraFOV={cameraFOV}
                onCameraSelect={handleCameraSelect}
                isEditingLocation={isEditingLocation}
                tempMarkerPosition={tempMarkerPosition}
                onPickLocation={handleMapPickLocation}
                onSelectedMarkerDragEnd={handleMarkerDragEnd}
                onViewChange={handleMapViewChange}
            />
        </div>
    );
};

export default CctvCameraMapPanel;
